import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Mail, MapPin, ArrowRight, Send, Check, DollarSign, UserCheck, Shield, ArrowUpRight,
  Paperclip, X, FileText,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import CallNowButton from "../components/CallNowButton";
import { SERVICES } from "../data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL = "quotes@gmprint.com.au";

// =========================================================================
// SHARED
// =========================================================================
const easeExpo = [0.16, 1, 0.3, 1];

const GMLogo = ({ size = 40, onDark = false }) => {
  const src = onDark ? "/logos/gm-logo-dark.png" : "/logos/gm-logo-light.png";
  return (
    <img
      src={src}
      alt="GM Print"
      width={size}
      height={size}
      style={{ display: "block" }}
      data-testid="gm-logo"
    />
  );
};

const CMYKBar = ({ className = "", thickness = "" }) => (
  <div className={`cmyk-bar ${thickness} ${className}`} data-testid="cmyk-bar">
    <div className="c" /><div className="m" /><div className="y" /><div className="k" />
  </div>
);

// Line-by-line masked reveal — the signature hero moment
const MaskedLine = ({ children, delay = 0 }) => (
  <span className="masked-line" aria-hidden="false">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: easeExpo, delay }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

// =========================================================================
// HERO — Kinetic reveal + spotlight image + subtle mouse parallax
// =========================================================================
const HeroSection = () => {
  const imgRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const sprX = useSpring(mouseX, { stiffness: 60, damping: 20 });
  const sprY = useSpring(mouseY, { stiffness: 60, damping: 20 });

  useEffect(() => {
    const handleMove = (e) => {
      if (!imgRef.current) return;
      const r = imgRef.current.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * 20;
      const y = ((e.clientY - r.top) / r.height - 0.5) * 20;
      mouseX.set(x);
      mouseY.set(y);
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, [mouseX, mouseY]);

  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 800], [0, 120]);

  return (
    <section id="top" className="hero-section-v2" data-testid="hero-section">
      <div className="hero-grid">
        {/* LEFT — Kinetic type */}
        <div className="hero-copy">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className="w-14 h-px bg-black" />
            <span className="label-caps">Brisbane's Print, Design &amp; Marketing Service</span>
          </motion.div>

          <h1 className="hero-headline" data-testid="hero-headline">
            <span className="hero-line"><MaskedLine delay={0.15}>Gain</MaskedLine></span>
            <span className="hero-line"><MaskedLine delay={0.3}>Momentum</MaskedLine></span>
            <span className="hero-line hero-line--accent">
              <MaskedLine delay={0.45}>With Every</MaskedLine>
            </span>
            <span className="hero-line hero-line--underline">
              <MaskedLine delay={0.6}>Print<span className="hero-dot">.</span></MaskedLine>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: easeExpo }}
            className="hero-sub"
          >
            <strong className="font-semibold text-black">Brisbane's one-stop print, design &amp; marketing service</strong> — everything you need under one roof. Send us a brief and we'll come back within 24 hours with a competitive quote. Servicing Queensland since 2015.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25, ease: easeExpo }}
            className="flex flex-wrap gap-4"
          >
            <a href="#contact" className="btn-primary" data-testid="hero-get-quote-btn">
              Get Your Free Quote <ArrowRight size={16} />
            </a>
            <a href={`mailto:${EMAIL}`} className="btn-outline" data-testid="hero-email-btn">
              <Mail size={16} /> Email Us
            </a>
          </motion.div>
        </div>

        {/* RIGHT — Spotlighted product photo */}
        <motion.div
          ref={imgRef}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.4, ease: easeExpo }}
          className="hero-visual"
          style={{ y: parallaxY }}
        >
          <div className="hero-frame">
            <motion.img
              src="https://images.pexels.com/photos/9550363/pexels-photo-9550363.jpeg"
              alt="Offset printing press producing colour print in Brisbane"
              style={{ x: sprX, y: sprY }}
              className="hero-photo"
            />
            <div className="hero-spotlight" />
            <div className="hero-frame-bar" />
          </div>
          <div className="hero-badge">
            <GMLogo size={64} />
          </div>
          {/* Vertical mono meta */}
          <div className="hero-vertical-meta">
            <span>BRISBANE / QLD</span>
            <span className="inline-block w-4 h-px bg-black/40 mx-3 align-middle" />
            <span>EST. 2015</span>
          </div>
        </motion.div>
      </div>

      {/* Bottom Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.5, ease: easeExpo }}
        className="hero-stats"
      >
        <div className="stat-block" data-testid="stat-projects">
          <div className="stat-number">500+</div>
          <div className="stat-label">Projects Delivered</div>
        </div>
        <div className="stat-block" data-testid="stat-savings">
          <div className="stat-number">28</div>
          <div className="stat-label">Years in the Industry</div>
        </div>
        <div className="stat-block" data-testid="stat-turnaround">
          <div className="stat-number">24hr</div>
          <div className="stat-label">Quote Turnaround</div>
        </div>
        <div className="stat-block hidden md:block">
          <div className="stat-number text-transparent bg-clip-text" style={{ WebkitTextStroke: "1.5px #000" }}>∞</div>
          <div className="stat-label">Print Possibilities</div>
        </div>
      </motion.div>
    </section>
  );
};

// =========================================================================
// SLOW EDITORIAL MARQUEE
// =========================================================================
const MarqueeSection = () => (
  <div className="marquee-editorial" data-testid="marquee-section">
    <Marquee speed={20} gradient={false}>
      <div className="marquee-editorial-text">
        <span>Printing</span>
        <div className="marquee-dot c" />
        <span>Design</span>
        <div className="marquee-dot m" />
        <span>Signage</span>
        <div className="marquee-dot y" />
        <span>Marketing</span>
        <div className="marquee-dot c" />
        <span>Brisbane</span>
        <div className="marquee-dot m" />
        <span>Since 2015</span>
        <div className="marquee-dot y" />
        <span>100% Australian Owned</span>
        <div className="marquee-dot c" />
        <span>Printing</span>
        <div className="marquee-dot m" />
        <span>Design</span>
        <div className="marquee-dot y" />
        <span>Signage</span>
        <div className="marquee-dot c" />
        <span>Marketing</span>
        <div className="marquee-dot m" />
      </div>
    </Marquee>
  </div>
);

// =========================================================================
// NUMBERED MANIFESTO
// =========================================================================
const chapters = [
  {
    number: "01",
    chapter: "The Brief",
    title: "You tell us what you need.",
    body: "Specs, quantities, deadlines. Files welcome. No jargon required. We speak plain English and print.",
  },
  {
    number: "02",
    chapter: "The Match",
    title: "We match it to the right press.",
    body: "Every print job is different. We match your brief to the press, paper and finish that will deliver the best result — and quote you within 24 hours.",
  },
  {
    number: "03",
    chapter: "The Delivery",
    title: "We manage every last detail.",
    body: "Production. Quality control. Logistics. Everything from proof to pallet handled by one team — ours.",
  },
];

const ManifestoSection = () => (
  <section id="manifesto" className="manifesto-section" data-testid="how-it-works-section">
    <div className="manifesto-header">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-14 h-px bg-black" />
        <span className="label-caps">Process</span>
      </div>
      <h2 className="font-display text-6xl md:text-7xl lg:text-8xl">
        <span className="block">Three steps.</span>
        <span className="block">Zero fuss.</span>
      </h2>
    </div>

    <div className="manifesto-chapters">
      {chapters.map((c, i) => (
        <motion.div
          key={c.number}
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: i * 0.08, ease: easeExpo }}
          className="manifesto-chapter"
          data-testid={`timeline-step-${i}`}
        >
          <div className="manifesto-chapter-inner">
            <div className="manifesto-chapter-meta">
              <div className="manifesto-number">{c.number}</div>
              <div className="manifesto-chapter-label">Step {c.number}</div>
              <div className="label-caps text-black/40">{c.chapter}</div>
            </div>
            <div className="manifesto-chapter-body">
              <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-6">{c.title}</h3>
              <p className="font-body text-lg text-black/70 max-w-xl leading-relaxed">{c.body}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

// =========================================================================
// SERVICES (DARK)
// =========================================================================
const services = [
  { title: "Business Cards", description: "First impressions, done right. Premium stocks, foils, embosses." },
  { title: "Signage", description: "Corflute, pull-ups, A-frames, acrylics—indoor and outdoor that lasts in the QLD sun." },
  { title: "Banners", description: "Vinyl, mesh, fabric. Any size, any event, any deadline." },
  { title: "Publications", description: "Yearbooks, prospectuses, magazines, newsletters, programs." },
  { title: "Promotional Items", description: "Merchandise that gets remembered. Full range, sourced smart." },
  { title: "Marketing Campaigns", description: "Digital + print campaigns for small businesses that actually convert." },
];
// Note: services now sourced from /data/services.js — kept for backwards compat in case of future use


const ServicesSection = () => (
  <section id="services" className="services-dark" data-testid="services-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36 relative">
      <div className="ghost-mark-large" aria-hidden="true">
        <GMLogo size={720} onDark />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: easeExpo }}
          className="md:col-span-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-px bg-white" />
            <span className="label-caps text-white">What We Do</span>
          </div>
          <h2 className="font-display text-6xl md:text-7xl lg:text-8xl">
            Print &amp; marketing<br />in Brisbane,<br />sorted.
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: easeExpo }}
          className="md:col-span-4 text-white/70 text-lg"
        >
          From a single business card to a full marketing campaign — we produce it all through our Brisbane print &amp; media team.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 relative z-10">
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.slug}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: i * 0.06, ease: easeExpo }}
            data-testid={`service-card-${i}`}
          >
            <Link to={`/${s.slug}`} className="service-tile block h-full" data-testid={`service-learn-more-${s.slug}`}>
              <div className="font-mono text-xs mb-6 tracking-widest text-white/40">0{i + 1}</div>
              <h3 className="font-display text-3xl mb-4 leading-[0.95]">{s.tileTitle}</h3>
              <p className="font-body text-sm text-white/60 leading-relaxed">{s.tileDescription}</p>
              <div className="mt-6 flex items-center gap-2 text-white/60 group-hover:text-white font-mono text-xs uppercase tracking-widest">
                <span>Learn more</span>
                <ArrowUpRight size={14} />
              </div>
            </Link>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

// =========================================================================
// INDUSTRIES
// =========================================================================
const industries = [
  { image: "https://images.pexels.com/photos/2982449/pexels-photo-2982449.jpeg", label: "Schools", title: "Christian Schools", description: "Prospectuses, yearbooks, event banners, fundraiser materials." },
  { image: "https://images.pexels.com/photos/1657322/pexels-photo-1657322.jpeg", label: "Sports Clubs", title: "Sports Organisations", description: "Event signage, sponsor banners, tournament programs." },
  { image: "https://images.pexels.com/photos/6256834/pexels-photo-6256834.jpeg", label: "Golf Clubs", title: "Golf & Country Clubs", description: "Scorecards, member directories, tournament programs." },
  { image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg", label: "SMB", title: "Small Businesses", description: "Digital + print marketing that gets results." },
  { image: "https://images.pexels.com/photos/1616113/pexels-photo-1616113.jpeg", label: "Events", title: "Event Organisers", description: "Full event print — signage, programs, wayfinding, promo." },
];

const IndustriesSection = () => (
  <section id="industries" className="bg-white border-b-2 border-black" data-testid="industries-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: easeExpo }}
        className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
      >
        <div className="md:col-span-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-px bg-black" />
            <span className="label-caps">Who We Serve</span>
          </div>
          <h2 className="font-display text-6xl md:text-7xl lg:text-8xl">
            Built for busy<br />Brisbane decision-makers.
          </h2>
        </div>
        <div className="md:col-span-4">
          <p className="text-black/70 text-lg">
            We work with organisations across Brisbane and Queensland who need reliable print and marketing without the hassle.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {industries.map((ind, i) => (
          <motion.div
            key={ind.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: i * 0.06, ease: easeExpo }}
            className="industry-card"
            data-testid={`industry-card-${i}`}
          >
            <div className="industry-image-wrapper">
              <img src={ind.image} alt={ind.title} className="industry-image" />
              <span className="industry-label">{ind.label}</span>
            </div>
            <div className="industry-content">
              <h3 className="font-display text-2xl mb-2">{ind.title}</h3>
              <p className="font-body text-sm text-black/70 leading-relaxed">{ind.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// =========================================================================
// FOUNDERS — Dan & Sarah Morris
// =========================================================================
const founders = [
  {
    name: "Dan Morris",
    role: "Printer by Trade",
    initials: "DM",
    tagline: "Ink under the fingernails.",
    bio: "Dan brings 28 years in the printing, signage and design industry — a printer by trade who knows presses, paper, and what \"good\" looks like from the plate up. That press-side experience is exactly why the quality never slips.",
    accent: "#00AEEF",
  },
  {
    name: "Sarah Morris",
    role: "Brand & Marketing Director",
    initials: "SM",
    tagline: "Growth by design.",
    bio: "Sarah has worked as a Brand and Marketing Director for over 15 years, helping her clients grow by generating more revenue through strategic marketing and promotion — the reason your print doesn't just look good, it works hard.",
    accent: "#EC008C",
  },
];

const FoundersSection = () => (
  <section id="founders" className="bg-white border-b-2 border-black" data-testid="founders-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: easeExpo }}
        className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
      >
        <div className="md:col-span-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-px bg-black" />
            <span className="label-caps">The Team</span>
          </div>
          <h2 className="font-display text-6xl md:text-7xl lg:text-8xl">
            Two founders.<br />One mission.
          </h2>
        </div>
        <div className="md:col-span-4">
          <p className="text-black/70 text-lg leading-relaxed">
            A husband-and-wife team combining decades of hands-on printing and strategic marketing. 100% Australian owned and operated. Servicing Brisbane since 2015.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {founders.map((f, i) => (
          <motion.article
            key={f.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: easeExpo }}
            className="founder-card"
            data-testid={`founder-card-${i}`}
          >
            <div className="founder-portrait" style={{ background: f.accent }}>
              <span className="founder-initials">{f.initials}</span>
              <span className="founder-portrait-label">{f.role.toUpperCase()}</span>
            </div>
            <div className="founder-body">
              <div className="font-mono text-[10px] uppercase tracking-widest text-black/40 mb-2">0{i + 1} · {f.role}</div>
              <h3 className="font-display text-4xl md:text-5xl mb-3 leading-[0.95]">{f.name}</h3>
              <p className="font-display text-xl md:text-2xl mb-6 text-black/60">{f.tagline}</p>
              <p className="font-body text-base text-black/70 leading-relaxed">{f.bio}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

// =========================================================================
// WHY BROKER
// =========================================================================
const reasons = [
  { icon: <DollarSign size={28} />, title: "Right Job, Right Press", description: "We match every job to the press, paper and finish that will deliver the best result. That means no costly mistakes — and your business always represented at its best." },
  { icon: <UserCheck size={28} />, title: "One Point of Contact", description: "One team. One brief. One quote. From artwork through to delivery, you deal with the same expert from start to finish." },
  { icon: <Shield size={28} />, title: "Real Print Expertise", description: "Dan brings 28 years in printing, signage and design as a printer by trade. Sarah brings 15+ years in brand and marketing. 100% Australian owned and operated — printing, design and marketing under one roof." },
];

const WhyBrokerSection = () => (
  <section id="why-broker" className="bg-[#0E0E0E] text-white" data-testid="why-broker-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: easeExpo }}
        className="mb-16 max-w-3xl"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-px bg-white" />
          <span className="label-caps text-white">Why Choose GM Print</span>
        </div>
        <h2 className="font-display text-6xl md:text-7xl lg:text-8xl mb-6">
          One team.<br />Every part of print.
        </h2>
        <p className="text-white/70 text-lg leading-relaxed">
          Print buying shouldn't be a full-time job. From concept and design through to production and delivery, our Brisbane print &amp; media team handles the lot — with the pricing power of a large-scale operation and the personal service of a boutique agency.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reasons.map((r, i) => (
          <motion.div
            key={r.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: easeExpo }}
            className="brand-card on-dark"
            data-testid={`why-broker-card-${i}`}
          >
            <div className="mb-6 text-[#00AEEF]">{r.icon}</div>
            <h3 className="font-display text-3xl mb-3">{r.title}</h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">{r.description}</p>
          </motion.div>
        ))}
      </div>

      {/* Testimonial hidden until real client quote available — keep design for reuse
      <motion.blockquote
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: easeExpo }}
        className="mt-24 border-2 border-white p-8 md:p-14 relative"
      >
        <div className="font-display text-9xl leading-none text-[#EC008C] absolute -top-6 left-8 select-none">"</div>
        <p className="font-display text-3xl md:text-5xl leading-tight mb-8 mt-6" data-testid="testimonial-quote">
          GM Print cut our print spend by a third and doubled our turnaround speed. Set and forget.
        </p>
        <div className="flex items-center gap-4">
          <div className="w-14 h-px bg-white" />
          <div>
            <div className="font-body font-semibold" data-testid="testimonial-author">Sarah Mitchell</div>
            <div className="font-mono text-xs text-white/60" data-testid="testimonial-role">Communications Director, St. Peter's College</div>
          </div>
        </div>
      </motion.blockquote>
      */}
    </div>
  </section>
);

// =========================================================================
// CONTACT
// =========================================================================
const ACCEPTED_FILE_EXTS = [".pdf", ".ai", ".eps", ".zip", ".indd", ".jpg", ".jpeg", ".png", ".tif", ".tiff"];
const MAX_FILE_MB = 25;
const MAX_FILES = 5;

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const addFiles = (incoming) => {
    const accepted = [];
    for (const f of Array.from(incoming || [])) {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      if (!ACCEPTED_FILE_EXTS.includes(ext)) { toast.error(`${f.name}: that file type isn't supported`); continue; }
      if (f.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`${f.name}: over the ${MAX_FILE_MB}MB limit`); continue; }
      accepted.push(f);
    }
    setFiles((prev) => {
      const merged = [...prev, ...accepted];
      if (merged.length > MAX_FILES) {
        toast.error(`Up to ${MAX_FILES} files — the extras were skipped`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("email", formData.email);
      fd.append("message", formData.message);
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/contact`, fd, {
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      toast.success("Quote request received! We'll be in touch within 24 hours.");
      setFormData({ name: "", email: "", message: "" });
      setFiles([]);
      setProgress(0);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-white" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: easeExpo }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-px bg-black" />
            <span className="label-caps">Get a Quote</span>
          </div>
          <h2 className="font-display text-6xl md:text-7xl lg:text-8xl mb-6">
            Ready when<br />you are.
          </h2>
          <p className="font-body text-black/70 text-lg leading-relaxed mb-10">
            Tell us what you need. We'll come back to you within 24 hours with a competitive quote.
          </p>

          <div className="space-y-5">
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 group" data-testid="contact-email">
              <div className="w-14 h-14 border-2 border-black flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                <Mail size={22} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">Email</div>
                <div className="font-body text-lg font-semibold">{EMAIL}</div>
              </div>
            </a>
            <a href="#contact" className="flex items-center gap-4 group" data-testid="contact-form-link">
              <div className="w-14 h-14 border-2 border-black flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                <Send size={22} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">Or Send a Brief</div>
                <div className="font-body text-lg font-semibold">Use the quote form &rarr;</div>
              </div>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-location">
              <div className="w-14 h-14 border-2 border-black flex items-center justify-center">
                <MapPin size={22} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">Location</div>
                <div className="font-body text-lg font-semibold">Brisbane, Queensland</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: easeExpo }}
          className="lg:col-span-7"
        >
          <form onSubmit={handleSubmit} className="border-2 border-black p-8 md:p-12 relative" data-testid="contact-form">
            <div className="absolute -top-4 left-8 bg-white px-4">
              <span className="label-caps">Quote Request</span>
            </div>
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="label-caps block mb-2">Full Name</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="input-field" placeholder="John Smith" data-testid="contact-name-input" />
              </div>
              <div>
                <label htmlFor="email" className="label-caps block mb-2">Email</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="input-field" placeholder="john@example.com" data-testid="contact-email-input" />
              </div>
              <div>
                <label htmlFor="message" className="label-caps block mb-2">What do you need?</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-field resize-none" placeholder="Tell us about the job — quantities, deadline, specs..." data-testid="contact-message-input" />
              </div>
              <div>
                <label className="label-caps block mb-2">Attach print files <span className="text-black/40">(optional)</span></label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_FILE_EXTS.join(",")}
                  className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                  data-testid="contact-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-black/30 hover:border-black px-5 py-6 flex items-center justify-center gap-3 transition-colors"
                  data-testid="contact-file-dropzone"
                >
                  <Paperclip size={18} />
                  <span className="font-body text-sm text-black/60">
                    PDF, AI, EPS, INDD, ZIP or images — up to {MAX_FILES} files, {MAX_FILE_MB}MB each
                  </span>
                </button>
                {files.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="contact-file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 border border-black/20 px-3 py-2" data-testid={`contact-file-chip-${i}`}>
                        <span className="flex items-center gap-2 text-sm font-body min-w-0">
                          <FileText size={14} className="shrink-0" />
                          <span className="truncate">{f.name}</span>
                          <span className="font-mono text-[10px] text-black/40 shrink-0">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                        </span>
                        <button type="button" onClick={() => removeFile(i)} className="text-black/40 hover:text-[#EC008C] transition-colors shrink-0" data-testid={`contact-file-remove-${i}`}>
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {isSubmitting && files.length > 0 && (
                  <div className="mt-3" data-testid="contact-upload-progress">
                    <div className="h-1.5 bg-black/10 w-full">
                      <div className="h-full bg-[#00AEEF] transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="font-mono text-[10px] text-black/50 mt-1">Uploading {progress}%</div>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="mt-0.5 w-4 h-4 accent-black cursor-pointer"
                  data-testid="contact-terms-checkbox"
                />
                <label htmlFor="terms" className="font-body text-sm text-black/60 cursor-pointer">
                  I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-black transition-colors">Terms of Service</a>
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-2">
                <button type="submit" disabled={isSubmitting} className="btn-primary disabled:opacity-50" data-testid="contact-submit-btn">
                  {isSubmitting ? "Sending..." : "Get Your Free Quote"} <Send size={16} />
                </button>
                <div className="flex items-center gap-2 text-sm text-black/60">
                  <Check size={16} className="text-black" />
                  <span className="font-mono text-xs">24hr response guaranteed</span>
                </div>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// =========================================================================
// PAGE
// =========================================================================
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <CallNowButton />
      <main>
        <HeroSection />
        <MarqueeSection />
        <ManifestoSection />
        <ServicesSection />
        <IndustriesSection />
        <FoundersSection />
        <WhyBrokerSection />
        <ContactSection />
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
